import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { type CampaignId, type LocationId } from '@brightlocal/shared';

import { BUILDER_STEPS } from 'shared/consts';
import { prefixKey } from 'shared/utils';

import { BUILDER_STORAGE_KEY } from '../consts';
import type {
  BuilderDeliverySpeed,
  BuilderPaymentMethod,
  BuilderPreference,
  BuilderQuestionnaireAnswers,
  BuilderStep
} from '../types';
import { getNextBuilderStep, getStepIndex } from '../utils';

/**
 * Bump on every breaking change to the persisted state shape (rename, retype,
 * remove, or change the accepted value set of a field). Old persisted state
 * is passed to `migrate` so we can clean it up rather than fall through with
 * stale values.
 */
const PERSIST_VERSION = 3;

type PersistedShape = Record<string, unknown>;

const isPersistedShape = (value: unknown): value is PersistedShape => typeof value === 'object' && value !== null;

/**
 * v2 drops `buildSelectionTouched` (the auto-fill no longer freezes on manual
 * touch — it re-derives). Strip the stale key from old persisted state so it
 * doesn't linger in localStorage.
 *
 * v3 drops `isOrderHydrated`. Stripping it in `partialize` only stops new writes; a
 * `true` already in a customer's storage would still skip hydration, so clear it here.
 *
 * A persisted `bundleQuantity` no longer offered by the campaign's package
 * catalog needs no migration — it's handled at the query boundary:
 * `mapToBundlePackageName` returns `undefined` for an unknown size, so estimate
 * and autosave omit `packageName` rather than send a value the BE rejects.
 */
export const migrateBuilderPersistedState = (persisted: unknown): PersistedShape => {
  if (!isPersistedShape(persisted)) return {};

  const { buildSelectionTouched: _removed, isOrderHydrated: _sessionOnly, ...rest } = persisted;

  return rest;
};

/** Persisted wizard state for a citation builder campaign — identity, step progress, and per-step selections. */
interface CitationBuilderData {
  // Primary identity — drives the URL and the persisted state slice.
  campaignId: CampaignId | null;
  // Owning location; the header uses it to fetch business name + address.
  locationId: LocationId | null;
  // Answers to the campaign questionnaire — how the user wants to serve listings.
  questionnaireAnswers: BuilderQuestionnaireAnswers;
  bundleQuantity: number | null;
  preferences: BuilderPreference[];
  // Per-step selections, keyed by directory/aggregator id.
  fixSelections: Record<string, boolean>;
  /**
   * Whether this campaign skips the Fix step entirely — a no-lookup country, or a
   * completed lookup with nothing to correct. Persisted so the route guard can read
   * it in `beforeLoad`, where the deriving queries aren't available.
   */
  isFixStepSkipped: boolean;
  buildSelections: Record<string, boolean>;
  // One-shot guard so the top-N highest-DA auto-fill only seeds once per load.
  buildSelectionInitialised: boolean;
  isDuplicateRemovalOn: boolean;
  selectedAggregators: Record<string, boolean>;
  // Active Sync is sold within the aggregators step but is a separate `is_active_sync` flag at the API.
  isActiveSyncOn: boolean;
  deliverySpeed: BuilderDeliverySpeed | null;
  hasAcceptedTerms: boolean;
  paymentMethod: BuilderPaymentMethod | null;
  // Furthest step the user may reach; advances forward only and gates step access.
  maxStep: BuilderStep;
  completedSteps: BuilderStep[];
  // Whether the saved order has been hydrated — guards hydration to run once per
  // entry (initForCampaign resets it, so a new campaign re-hydrates).
  isOrderHydrated: boolean;
  // GA4 `item_id`s already reported via add_to_cart, so re-adding an existing
  // line never emits a duplicate while a genuinely new one still does.
  addedToCartItemIds: string[];
}

/** Arguments for seeding the store when a campaign flow is entered. */
interface InitForCampaignArgs {
  campaignId: CampaignId;
  // Optional — lets the header render without an extra fetch when the entry handler already has it.
  locationId?: LocationId;
}

/** Mutations exposed by the store for driving the wizard. */
interface CitationBuilderActions {
  // Seeds the store for a campaign; called by entry handlers before navigating in.
  initForCampaign: (args: InitForCampaignArgs) => void;
  // Wipes all step state and seats a new campaignId (URL campaignId ≠ stored one).
  resetForCampaign: (campaignId: CampaignId) => void;
  setLocationId: (locationId: LocationId) => void;
  setQuestionnaireAnswers: (answers: BuilderQuestionnaireAnswers) => void;
  setBundleQuantity: (quantity: number) => void;
  setPreferences: (preferences: BuilderPreference[]) => void;
  setIsFixStepSkipped: (isSkipped: boolean) => void;
  toggleFix: (directoryId: string) => void;
  // Replaces the fix selection map wholesale — used by hydration.
  setFixSelections: (selections: Record<string, boolean>) => void;
  toggleBuild: (directoryId: string) => void;
  // Replaces the build selection map wholesale — used by the auto-fill / refill flow.
  setBuildSelections: (selections: Record<string, boolean>) => void;
  markBuildSelectionInitialised: VoidFunction;
  setIsDuplicateRemovalOn: (on: boolean) => void;
  toggleAggregator: (aggregatorId: string) => void;
  // Replaces the aggregator selection map wholesale — used by the "Add all" action.
  setSelectedAggregators: (selections: Record<string, boolean>) => void;
  // Toggles the Active Sync add-on on the Aggregators step.
  setActiveSyncOn: (on: boolean) => void;
  // Changing the speed also clears the terms acceptance: the campaign terms text differs between standard and Express.
  setDeliverySpeed: (speed: BuilderDeliverySpeed) => void;
  setHasAcceptedTerms: (accepted: boolean) => void;
  setPaymentMethod: (method: BuilderPaymentMethod) => void;
  // Marks a step completed and advances maxStep forward only — never regresses.
  markStepCompleted: (step: BuilderStep) => void;
  // Flips isOrderHydrated once the saved order has been applied to the store.
  markOrderHydrated: VoidFunction;
  // Records the GA4 item ids reported via add_to_cart so they aren't re-reported.
  markItemsAddedToCart: (itemIds: string[]) => void;
  reset: VoidFunction;
}

type CitationBuilderState = CitationBuilderData & CitationBuilderActions;

const initialData: CitationBuilderData = {
  campaignId: null,
  locationId: null,
  questionnaireAnswers: { serve: null },
  bundleQuantity: null,
  preferences: [],
  fixSelections: {},
  isFixStepSkipped: false,
  buildSelections: {},
  buildSelectionInitialised: false,
  // Pre-selected for new campaigns; a saved order overrides this on hydration.
  isDuplicateRemovalOn: true,
  selectedAggregators: {},
  isActiveSyncOn: false,
  deliverySpeed: null,
  hasAcceptedTerms: false,
  paymentMethod: null,
  maxStep: BUILDER_STEPS.SCAN,
  completedSteps: [],
  isOrderHydrated: false,
  addedToCartItemIds: []
};

const addCompletedStep = (completedSteps: BuilderStep[], step: BuilderStep) =>
  completedSteps.includes(step) ? completedSteps : [...completedSteps, step];

const advanceMaxStep = (current: BuilderStep, candidate: BuilderStep) =>
  getStepIndex(candidate) > getStepIndex(current) ? candidate : current;

/**
 * Completing step N unlocks step N+1 — that's the contract the step-gate guard
 * (`isStepAccessible`) reads. If we only set `maxStep` to N here, the user
 * would be bounced back from N+1's route. Last step has no successor; we stop
 * at the last step's index.
 */
const maxStepAfterCompleting = (current: BuilderStep, completed: BuilderStep) => {
  const unlocked = getNextBuilderStep(completed) ?? completed;

  return advanceMaxStep(current, unlocked);
};

/**
 * Strips the state that must not outlive the session:
 *
 * - `fixSelections` / `buildSelections` — the saved campaign at the API is the single
 *   source of truth for what's selected (`useAutosaveCampaign` writes it,
 *   `useHydrateCampaignFromSaved` reads it back). A local copy is a second, staler
 *   answer to the same question, and it would also smuggle click history across a
 *   bundle change, which must not survive it.
 * - `isFixStepSkipped` — derived from the lookup. The route guard reads it in
 *   `beforeLoad`, before the effect that recomputes it runs, so a stale `true` would
 *   bounce a legitimate navigation to a Fix step that now has rows.
 * - `isOrderHydrated` — a session fact, not a preference. Persisted, a revisit to the
 *   same campaign skipped hydration (the route only re-inits when `campaignId`
 *   changes), so autosave — gated on this flag — flushed a store whose selections had
 *   been stripped, wiping the saved build citations server-side.
 * @param state - The full store state on its way to storage.
 */
export const partializeBuilderState = <
  T extends {
    isFixStepSkipped: boolean;
    fixSelections: unknown;
    buildSelections: unknown;
    isOrderHydrated: boolean;
  }
>({
  isFixStepSkipped: _derived,
  fixSelections: _fixFromApi,
  buildSelections: _buildFromApi,
  isOrderHydrated: _sessionOnly,
  ...persisted
}: T) => persisted;

export const useCitationBuilderStore = create<CitationBuilderState>()(
  persist(
    immer((set, get) => ({
      ...initialData,
      initForCampaign: ({ campaignId, locationId }) =>
        set(() => ({ ...initialData, campaignId, locationId: locationId ?? null })),
      resetForCampaign: (campaignId) => get().initForCampaign({ campaignId }),
      setLocationId: (locationId) =>
        set((state) => {
          state.locationId = locationId;
        }),
      setQuestionnaireAnswers: (answers) =>
        set((state) => {
          state.questionnaireAnswers = answers;
        }),
      setBundleQuantity: (quantity) =>
        set((state) => {
          state.bundleQuantity = quantity;
        }),
      setPreferences: (preferences) =>
        set((state) => {
          state.preferences = preferences;
        }),
      setIsFixStepSkipped: (isSkipped) =>
        set((state) => {
          state.isFixStepSkipped = isSkipped;
        }),
      toggleFix: (directoryId) =>
        set((state) => {
          // Fix rows are default-on: an unstored id reads as selected (the list
          // filters on `!== false`). So flip from the *effective* state — an
          // unstored (undefined) row is currently ON, so the first toggle must
          // turn it OFF. `!fixSelections[id]` would read `!undefined === true`
          // and leave it selected, making the first uncheck a no-op.
          state.fixSelections[directoryId] = state.fixSelections[directoryId] === false;
        }),
      setFixSelections: (selections) =>
        set((state) => {
          state.fixSelections = selections;
        }),
      toggleBuild: (directoryId) =>
        set((state) => {
          state.buildSelections[directoryId] = !state.buildSelections[directoryId];
        }),
      setBuildSelections: (selections) =>
        set((state) => {
          state.buildSelections = selections;
        }),
      markBuildSelectionInitialised: () =>
        set((state) => {
          state.buildSelectionInitialised = true;
        }),
      setIsDuplicateRemovalOn: (on) =>
        set((state) => {
          state.isDuplicateRemovalOn = on;
        }),
      toggleAggregator: (aggregatorId) =>
        set((state) => {
          state.selectedAggregators[aggregatorId] = !state.selectedAggregators[aggregatorId];
        }),
      setSelectedAggregators: (selections) =>
        set((state) => {
          state.selectedAggregators = selections;
        }),
      setActiveSyncOn: (on) =>
        set((state) => {
          state.isActiveSyncOn = on;
        }),
      setDeliverySpeed: (speed) =>
        set((state) => {
          // Only a real change invalidates the acceptance, so hydration re-applying
          // the saved speed keeps a ticked checkbox.
          const isSpeedChanged = state.deliverySpeed !== speed;

          if (isSpeedChanged) state.hasAcceptedTerms = false;
          state.deliverySpeed = speed;
        }),
      setHasAcceptedTerms: (accepted) =>
        set((state) => {
          state.hasAcceptedTerms = accepted;
        }),
      setPaymentMethod: (method) =>
        set((state) => {
          state.paymentMethod = method;
        }),
      markStepCompleted: (step) =>
        set((state) => {
          state.completedSteps = addCompletedStep(state.completedSteps, step);
          state.maxStep = maxStepAfterCompleting(state.maxStep, step);
        }),
      markOrderHydrated: () =>
        set((state) => {
          state.isOrderHydrated = true;
        }),
      markItemsAddedToCart: (itemIds) =>
        set((state) => {
          state.addedToCartItemIds = [...new Set([...state.addedToCartItemIds, ...itemIds])];
        }),
      reset: () => set(initialData)
    })),
    {
      name: prefixKey(BUILDER_STORAGE_KEY),
      version: PERSIST_VERSION,
      migrate: migrateBuilderPersistedState,
      partialize: partializeBuilderState
    }
  )
);
